import { Link } from 'react-router-dom'
import HomepageSectionHeader from '../components/homepage/HomepageSectionHeader'
import QuothaAvatar from '../components/quotha/QuothaAvatar'
import { usePageMetadata } from '../hooks/usePageMetadata'

const heroHighlights = [
  'Explore significant places through connected context, not isolated entries.',
  'Meet the figures who shaped those places and the stories around them.',
  'Ask better historical questions through guided conversation.',
]

const pillars = [
  {
    title: 'Historic Places',
    body:
      'From monuments and battlefields to cities and landscapes, each place is a starting point for deeper context rather than a dead-end description.',
  },
  {
    title: 'Historical Figures',
    body:
      'Places are shaped by people. The platform links sites to the rulers, witnesses, writers, and communities that give them human meaning.',
  },
  {
    title: 'Guided Conversation',
    body:
      'You can keep asking questions as you go, with conversations designed to support curiosity without collapsing history into confident fiction.',
  },
]

const principles = [
  {
    title: 'Grounded before fluent',
    body:
      'A polished answer is not enough on its own. The experience is designed to stay anchored to the historical record first.',
  },
  {
    title: 'Interpretation is not evidence',
    body:
      'Narrative and voice can help bring the past closer, but they should never pretend to be the source itself.',
  },
  {
    title: 'Uncertainty stays visible',
    body:
      'When the record is partial, debated, or inconclusive, that uncertainty should remain part of the experience.',
  },
  {
    title: 'Context matters',
    body:
      'A person, event, or quotation changes meaning when removed from its place, period, and surrounding conditions.',
  },
]

const journeySteps = [
  'Start with a place that matters.',
  'Follow the people connected to it.',
  'Carry that context into conversation.',
]

export default function About() {
  usePageMetadata({
    title: 'About Places in Time',
    description:
      'Learn about Places in Time, a platform for exploring historic places, meeting the figures behind them, and asking better historical questions through grounded AI conversation.',
    path: '/about',
  })

  return (
    <main className="page about-page">
      <section className="about-hero">
        <div className="about-hero-copy">
          <p className="eyebrow">About Places in Time</p>
          <h1>History you can explore, question, and talk to.</h1>
          <p className="about-hero-lede">
            Places in Time is a platform for exploring historic places, the people connected to them, and
            evidence-guided AI conversation that helps visitors ask better historical questions.
          </p>
          <p className="about-hero-support">
            Explore places. Meet the people behind them. Ask better questions.
          </p>
          <div className="button-row about-hero-actions">
            <Link className="button primary" to="/places">
              Explore Places
            </Link>
            <Link className="button" to="/people">
              Meet the Figures
            </Link>
            <Link className="button ghost" to="/chat">
              Start a Conversation
            </Link>
          </div>
        </div>

        <div className="about-hero-visual">
          <figure className="about-hero-image-card">
            <img
              src="/images/home/homepagebanner-1280.webp"
              alt="Visitor exploring history through conversation at a historic setting"
              loading="eager"
            />
            <figcaption>Historic places, connected figures, grounded conversation.</figcaption>
          </figure>

          <div className="about-hero-sidecards">
            <article className="about-mini-card">
              <p className="about-mini-label">What you can do</p>
              <ul className="about-mini-list">
                {heroHighlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>

            <article className="about-mini-card about-mini-card-quiet">
              <p className="about-mini-label">Why it feels different</p>
              <p>
                Places in Time keeps place, person, and question in the same frame, so the past feels
                connected instead of fragmented.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="about-section">
        <HomepageSectionHeader
          eyebrow="What Places in Time is"
          title="A public-facing history platform built around place, person, and conversation."
          intro="It brings browsing, biography, and guided questioning into one experience, so visitors can follow history through connections instead of hopping between disconnected sources."
        />

        <div className="about-explainer-grid">
          <div className="about-explainer-copy">
            <p className="about-explainer-lead">
              Places in Time starts with real places, links them to the figures around them, and lets
              curiosity continue in conversation without losing the thread.
            </p>
            <p>
              It is designed for people who want more than a flat summary and less than a wall of specialist
              material: a clearer way to discover the past, follow relationships, and keep asking useful
              questions.
            </p>
          </div>

          <div className="about-explainer-points" aria-label="How the experience comes together">
            <article>
              <span>01</span>
              <p>Browse important places through structured, context-rich pages.</p>
            </article>
            <article>
              <span>02</span>
              <p>Move into the people connected to those places and see history become human.</p>
            </article>
            <article>
              <span>03</span>
              <p>Keep the thread going in conversation shaped by evidence, perspective, and context.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="about-section">
        <HomepageSectionHeader
          eyebrow="The experience"
          title="Three ways into the same story."
          intro="The platform is built around three connected pillars, each designed to deepen the next."
        />

        <div className="home-value-grid about-pillars-grid">
          {pillars.map((pillar, index) => (
            <article key={pillar.title} className={`home-value-card about-pillar-card about-pillar-card-${index + 1}`}>
              <p className="about-card-kicker">0{index + 1}</p>
              <h3>{pillar.title}</h3>
              <p>{pillar.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section about-mission-panel">
        <div className="about-mission-mark">
          <p className="eyebrow">Why it exists</p>
          <h2>History is often encountered in fragments.</h2>
        </div>

        <div className="about-mission-copy">
          <p>
            A location appears without the people who shaped it. A biography loses the setting that gave it
            meaning. A summary sounds certain without showing where interpretation begins and evidence ends.
          </p>
          <p>
            Places in Time is designed to bring those layers back together in a single public experience, so
            discovery feels richer, more connected, and more honest about what is known, debated, or still
            unresolved.
          </p>
        </div>
      </section>

      <section className="about-section">
        <HomepageSectionHeader
          eyebrow="How it stays grounded"
          title="Built to keep confidence in proportion."
          intro="The aim is not to flatten history into neat answers, but to make exploration more useful, more readable, and more trustworthy."
        />

        <div className="about-principles-grid">
          {principles.map((principle) => (
            <article key={principle.title} className="about-principle-card">
              <h3>{principle.title}</h3>
              <p>{principle.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section about-showcase">
        <div className="about-showcase-media">
          <img
            src="/images/home/Chat demo.png"
            alt="Places in Time conversation view showing a guided history chat with source context"
            loading="lazy"
          />
        </div>

        <div className="about-showcase-copy">
          <HomepageSectionHeader
            eyebrow="A product you can use"
            title="One journey, not three separate tools."
            intro="Browse a place, follow its connected figures, then continue the thread in conversation. Each step is meant to add context to the next instead of making you start over."
          />

          <div className="about-showcase-flow" aria-label="Typical journey through the platform">
            {journeySteps.map((step, index) => (
              <article key={step}>
                <span>0{index + 1}</span>
                <p>{step}</p>
              </article>
            ))}
          </div>

          <article className="about-quotha-note">
            <QuothaAvatar size={84} />
            <div>
              <p className="about-mini-label">A guide with a point of view</p>
              <p>
                Quotha gives the conversational side of Places in Time a recognisable voice, while the
                platform keeps voice, interpretation, and evidence distinct.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className="about-cta">
        <p className="eyebrow">Start exploring</p>
        <h2>Start with a place. Leave with a better question.</h2>
        <p>
          The clearest way to understand Places in Time is to use it: follow a place, meet the figures around
          it, or begin a conversation and see where the history leads.
        </p>
        <div className="button-row about-cta-actions">
          <Link className="button primary" to="/places">
            Explore the Places
          </Link>
          <Link className="button" to="/people">
            Meet the Figures
          </Link>
          <Link className="button ghost" to="/guest/quotha">
            Talk to Quotha
          </Link>
        </div>
      </section>
    </main>
  )
}
